import type { EntityMappingDescriptor, EntityMappingKey } from '../models/entityMapping.types'

const campaignPath = (workspaceId: number | string, variant = ''): string =>
  `/metadata/campaign-mapping/${workspaceId}/${variant ? `${variant}/` : ''}`

export const ENTITY_MAPPING_DESCRIPTORS = {
  ga4: {
    key: 'ga4',
    entity: 'campaign',
    idKey: 'campaign_id',
    nameKey: 'campaign_name',
    path: (workspaceId) => campaignPath(workspaceId),
    unmappedKey: 'unmapped_campaigns',
    optionsCollectionKey: 'ga4_campaigns',
    scheduleReportId: 6,
    fields: [
      {
        draftKey: 'sources',
        payloadKey: 'ga4_sessionsource',
        optionValueKey: 'ga4_sessionsource',
        labelKey: 'workspaceDefinitions.mapping.fields.source',
        required: true,
      },
      {
        draftKey: 'mediums',
        payloadKey: 'ga4_sessionmedium',
        optionValueKey: 'ga4_sessionmedium',
        labelKey: 'workspaceDefinitions.mapping.fields.medium',
        required: true,
      },
      {
        draftKey: 'ga4_campaigns',
        payloadKey: 'ga4_campaign_name',
        optionValueKey: 'ga4_sessioncampaignname',
        labelKey: 'workspaceDefinitions.mapping.fields.campaign',
        required: true,
      },
    ],
    titleKey: 'workspaceDefinitions.mapping.ga4.title',
    rowTitleKey: 'workspaceDefinitions.mapping.campaignRow',
    logo: 'ga4',
  },
  bq: {
    key: 'bq',
    entity: 'campaign',
    idKey: 'campaign_id',
    nameKey: 'campaign_name',
    path: (workspaceId) => campaignPath(workspaceId, 'bq'),
    unmappedKey: 'unmapped_campaigns',
    optionsCollectionKey: 'bq_campaigns',
    scheduleReportId: 6,
    fields: [
      {
        draftKey: 'sources',
        payloadKey: 'bq_source',
        optionValueKey: 'bigquery_fp_source',
        labelKey: 'workspaceDefinitions.mapping.fields.source',
        required: true,
      },
      {
        draftKey: 'mediums',
        payloadKey: 'bq_medium',
        optionValueKey: 'bigquery_fp_medium',
        labelKey: 'workspaceDefinitions.mapping.fields.medium',
        required: true,
      },
      {
        draftKey: 'bq_campaign_name',
        payloadKey: 'bq_campaign_name',
        optionValueKey: 'bigquery_fp_campaign_name',
        labelKey: 'workspaceDefinitions.mapping.fields.campaign',
        required: true,
      },
    ],
    titleKey: 'workspaceDefinitions.mapping.bq.title',
    rowTitleKey: 'workspaceDefinitions.mapping.campaignRow',
    logo: 'bq',
  },
  mmp: {
    key: 'mmp',
    entity: 'campaign',
    idKey: 'campaign_id',
    nameKey: 'campaign_name',
    path: (workspaceId) => campaignPath(workspaceId, 'mmp'),
    unmappedKey: 'unmapped_campaigns',
    optionsCollectionKey: 'raw_campaigns',
    scheduleReportId: 6,
    fields: [
      {
        draftKey: 'raw_campaigns',
        payloadKey: 'mmp_campaign_name',
        optionValueKey: null,
        labelKey: 'workspaceDefinitions.mapping.fields.campaign',
        required: true,
      },
    ],
    titleKey: 'workspaceDefinitions.mapping.mmp.title',
    rowTitleKey: 'workspaceDefinitions.mapping.campaignRow',
    logo: 'adjust',
  },
  mail: {
    key: 'mail',
    entity: 'campaign',
    idKey: 'campaign_id',
    nameKey: 'campaign_name',
    path: (workspaceId) => campaignPath(workspaceId, 'mail'),
    unmappedKey: 'unmapped_campaigns',
    optionsCollectionKey: 'mail_campaigns',
    scheduleReportId: 6,
    fields: [
      {
        draftKey: 'sources',
        payloadKey: 'mail_source',
        optionValueKey: 'mail_utm_source',
        labelKey: 'workspaceDefinitions.mapping.fields.source',
        required: true,
        filterEmpty: true,
      },
      {
        draftKey: 'mediums',
        payloadKey: 'mail_medium',
        optionValueKey: 'mail_utm_medium',
        labelKey: 'workspaceDefinitions.mapping.fields.medium',
        required: true,
        filterEmpty: true,
      },
      {
        draftKey: 'mail_utm_campaign',
        payloadKey: 'mail_utm_campaign',
        optionValueKey: 'mail_utm_campaign',
        labelKey: 'workspaceDefinitions.mapping.fields.campaign',
        required: true,
      },
    ],
    titleKey: 'workspaceDefinitions.mapping.mail.title',
    rowTitleKey: 'workspaceDefinitions.mapping.campaignRow',
    logo: 'mail',
  },
  bqAdgroup: {
    key: 'bqAdgroup',
    entity: 'adgroup',
    idKey: 'adgroup_id',
    nameKey: 'adgroup_name',
    contextNameKey: 'campaign_name',
    path: (workspaceId) => `/adgroup-mapping/${workspaceId}/bq`,
    unmappedKey: 'unmapped_adgroups',
    optionsCollectionKey: 'bq_adgroups',
    scheduleReportId: 68,
    fields: [
      {
        draftKey: 'bq_adgroups',
        payloadKey: 'bigquery_adgroup_name',
        optionValueKey: null,
        labelKey: 'workspaceDefinitions.mapping.fields.adgroup',
        required: true,
      },
    ],
    titleKey: 'workspaceDefinitions.mapping.bqAdgroup.title',
    rowTitleKey: 'workspaceDefinitions.mapping.adgroupRow',
    logo: 'bq',
  },
} as const satisfies Record<EntityMappingKey, EntityMappingDescriptor>

export const entityMappingDescriptors: readonly EntityMappingDescriptor[] = [
  ENTITY_MAPPING_DESCRIPTORS.ga4,
  ENTITY_MAPPING_DESCRIPTORS.mail,
  ENTITY_MAPPING_DESCRIPTORS.mmp,
  ENTITY_MAPPING_DESCRIPTORS.bq,
  ENTITY_MAPPING_DESCRIPTORS.bqAdgroup,
]
